// --- START OF FILE HomeView.js ---

import React from 'react';
import { Play, Ghost, LogOut, Clock, Loader, BarChart2, ShieldCheck, TrendingUp, Trophy, Settings, RefreshCw, Check, X, Mail, Target } from 'lucide-react';
import DesktopHomeView from './DesktopHomeView';

const PlayerStatsSummaryCard = ({ currentUserData, matchHistory }) => {
    if (!currentUserData) return null;

    const realMatches = (matchHistory || []).filter(m => m.matchData.player2Id !== 'ghost');
    const practiceMatches = (matchHistory || []).filter(m => m.matchData.player2Id === 'ghost');

    let wins = 0;
    let losses = 0;
    let streakType = null;
    let streakCount = 0;

    if (realMatches.length > 0) {
        const sortedRealMatches = [...realMatches].sort((a, b) => new Date(b.completedAt?.toDate() || b.gameState.endTime) - new Date(a.completedAt?.toDate() || a.gameState.endTime));
        for (let i = 0; i < sortedRealMatches.length; i++) {
            const match = sortedRealMatches[i];
            const playerHandle = match.matchData.player1Id === currentUserData.uid ? 'player1' : 'player2';
            const won = match.gameState.winner === playerHandle;

            if (i === 0) {
                streakType = won ? 'W' : 'L';
                streakCount = 1;
            } else {
                const currentResult = won ? 'W' : 'L';
                if (streakType === currentResult) {
                    streakCount++;
                } else {
                    break; 
                }
            }
        }
        
        wins = realMatches.filter(match => {
             const playerHandle = match.matchData.player1Id === currentUserData.uid ? 'player1' : 'player2';
             return match.gameState.winner === playerHandle;
        }).length;
        losses = realMatches.length - wins;
    }

    const practiceWins = practiceMatches.filter(m => {
        const playerHandle = m.matchData.player1Id === currentUserData.uid ? 'player1' : 'player2';
        return m.gameState.winner === playerHandle;
    }).length;
    const practiceLosses = practiceMatches.length - practiceWins;

    return (
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700">
            <h2 className="text-lg font-bold truncate mb-3">
                {currentUserData.name}'s Dashboard
            </h2>
            <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-1">
                        <Trophy size={16} /> W/L Record
                    </div>
                    <div className="text-2xl font-bold">
                        <span className="text-green-400">{wins}</span>
                        <span className="text-slate-500 mx-1">-</span>
                        <span className="text-red-400">{losses}</span>
                    </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                     <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-1">
                        <TrendingUp size={16} /> Current Streak
                    </div>
                    {streakType ? (
                         <div className={`text-2xl font-bold ${streakType === 'W' ? 'text-green-400' : 'text-red-400'}`}>
                            {streakCount} {streakType}
                        </div>
                    ) : (
                        <div className="text-xl font-bold text-slate-300">-</div>
                    )}
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-1">
                    <Ghost size={18} /> Ghost W/L
                  </div>
                  <div className="text-2xl font-bold">
                    <span className="text-green-400">{practiceWins}</span>
                    <span className="text-slate-500 mx-1">-</span>
                    <span className="text-red-400">{practiceLosses}</span>
                  </div>
                </div>
            </div>
        </div>
    );
};

const PendingInvites = ({ invites, onAcceptChallenge, onDeclineChallenge, currentUserData }) => {
    if (!invites || invites.length === 0) {
        return null;
    }

    return (
        <div className="bg-blue-900/40 rounded-xl p-4 mb-6 border border-blue-600">
             <h2 className="text-lg font-bold text-center mb-3 flex items-center justify-center gap-2">
                <Mail size={20} /> Pending Challenges
            </h2>
            <div className="space-y-3">
                {invites.map(invite => {
                    const challengerName = invite.matchData.player1Id === currentUserData.uid 
                        ? invite.matchData.player2 
                        : invite.matchData.player1;

                    return (
                        <div key={invite.id} className="bg-slate-700/50 p-3 rounded-lg flex items-center justify-between">
                            <div>
                                <p className="font-bold">{challengerName}</p>
                                <p className="text-xs text-slate-400">
                                    {invite.matchData.gameType} | Race to {invite.matchData.player1RaceTo}-{invite.matchData.player2RaceTo}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => onAcceptChallenge(invite.id)} className="p-2 bg-green-600 rounded-lg hover:bg-green-700">
                                    <Check size={20} />
                                </button>
                                <button onClick={() => onDeclineChallenge(invite.id)} className="p-2 bg-red-600 rounded-lg hover:bg-red-700">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


export const HomeView = ({ 
    currentUserData, 
    onStartNewMatch, 
    onLogout, 
    matchHistory, 
    loadingHistory, 
    invites,
    onViewStats, 
    onViewPractice,
    onViewDrill,
    onReviewMatch,
    isMatchInProgress,
    onResumeMatch,
    onNavigateToProfile,
    onAcceptChallenge,
    onDeclineChallenge
}) => {
    const formatDate = (timestamp) => {
        if (timestamp && typeof timestamp.toDate === 'function') {
            return new Date(timestamp.toDate()).toLocaleDateString();
        }
        if (timestamp) {
            return new Date(timestamp).toLocaleDateString();
        }
        return 'N/A';
    };

    return (
        <>
            {/* Desktop Layout */}
            <div className="hidden lg:block">
                <DesktopHomeView 
                    currentUserData={currentUserData}
                    onStartNewMatch={onStartNewMatch}
                    onLogout={onLogout}
                    matchHistory={matchHistory}
                    loadingHistory={loadingHistory}
                    invites={invites}
                    onViewStats={onViewStats}
                    onViewPractice={onViewPractice}
                    onViewDrill={onViewDrill}
                    onReviewMatch={onReviewMatch}
                    isMatchInProgress={isMatchInProgress}
                    onResumeMatch={onResumeMatch}
                    onNavigateToProfile={onNavigateToProfile}
                    onAcceptChallenge={onAcceptChallenge}
                    onDeclineChallenge={onDeclineChallenge}
                    PlayerStatsSummaryCard={PlayerStatsSummaryCard}
                    PendingInvites={PendingInvites}
                    formatDate={formatDate}
                />
            </div>

            {/* Mobile Layout */}
            <div className="lg:hidden max-w-xl mx-auto p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl sm:text-3xl font-bold truncate">
                        Welcome!
                    </h1>
                    
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={onNavigateToProfile}
                            className="p-2 rounded-full hover:bg-slate-700 transition-colors" 
                            title="Profile & Settings"
                        >
                            <Settings className="w-5 h-5 text-slate-400 hover:text-white" />
                        </button>
                        <button 
                            onClick={onLogout} 
                            className="p-2 rounded-full hover:bg-slate-700 transition-colors" 
                            title="Logout"
                        >
                            <LogOut className="w-5 h-5 text-slate-400 hover:text-white" />
                        </button>
                    </div>
                </div>

            <PlayerStatsSummaryCard currentUserData={currentUserData} matchHistory={matchHistory} />
            
            <PendingInvites 
                invites={invites}
                currentUserData={currentUserData}
                onAcceptChallenge={onAcceptChallenge}
                onDeclineChallenge={onDeclineChallenge}
            />

            <div className="space-y-4">
                {isMatchInProgress ? (
                    <button 
                        onClick={onResumeMatch} 
                        className="w-full bg-yellow-500 text-black py-4 rounded-xl font-semibold text-lg hover:bg-yellow-600 transition-all flex items-center justify-center gap-3 shadow-lg"
                    >
                        <RefreshCw className="w-6 h-6" /> Resume Match
                    </button>
                ) : (
                    <button 
                        onClick={onStartNewMatch} 
                        className="w-full bg-green-600 text-white py-4 rounded-xl font-semibold text-lg hover:bg-green-700 transition-all flex items-center justify-center gap-3 shadow-lg"
                    >
                        <Play className="w-6 h-6" /> Start New Match
                    </button>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button 
                        onClick={onViewStats} 
                        className="w-full bg-purple-600 text-white py-3 rounded-xl font-medium hover:bg-purple-700 transition-all flex items-center justify-center gap-3"
                    >
                        <BarChart2 className="w-5 h-5" /> View Stats
                    </button>
                    <button 
                        onClick={onViewPractice} 
                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-3"
                    >
                        <Ghost className="w-5 h-5" /> Practice Stats
                    </button>
                    <button 
                        onClick={onViewDrill} 
                        className="w-full bg-orange-600 text-white py-3 rounded-xl font-medium hover:bg-orange-700 transition-all flex items-center justify-center gap-3"
                    >
                        <Target className="w-5 h-5" /> Drill Tracker
                    </button>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6 border border-slate-700">
                    <h2 className="text-lg font-bold text-center mb-4">Match History</h2>
                    {loadingHistory ? (
                        <div className="flex justify-center items-center p-4">
                            <Loader className="animate-spin text-slate-400" />
                            <span className="ml-2 text-slate-400">Loading history...</span>
                        </div>
                    ) : matchHistory && matchHistory.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                            {matchHistory.map(match => {
                                const isPlayer1 = match.matchData.player1Id === currentUserData.uid;
                                const opponentName = isPlayer1 ? match.matchData.player2 : match.matchData.player1;
                                const opponentIsProfile = isPlayer1 ? match.matchData.player2IsProfile : match.matchData.player1IsProfile;
                                
                                const myScore = isPlayer1 ? match.gameState.player1Score : match.gameState.player2Score;
                                const opponentScore = isPlayer1 ? match.gameState.player2Score : match.gameState.player1Score;
                                
                                const isWin = match.gameState.winner === (isPlayer1 ? 'player1' : 'player2');
                                const isSimpleTracked = match.matchData.trackingMode === 'simple';
                                const isForfeited = match.gameState.forfeited;

                                return (
                                <button 
                                    key={match.id} 
                                    className={`w-full text-left bg-slate-700/50 p-3 rounded-lg transition-colors ${
                                        isSimpleTracked 
                                            ? 'cursor-not-allowed opacity-70' 
                                            : 'hover:bg-slate-700/70'
                                    }`}
                                    onClick={() => onReviewMatch(match)}
                                    disabled={isSimpleTracked}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex flex-col">
                                            <div className="font-bold text-white flex items-center gap-2">
                                                {match.matchData.gameType}
                                                {isSimpleTracked && <span className="text-xs font-normal bg-slate-600 px-1.5 py-0.5 rounded-full text-slate-300">Score Only</span>}
                                            </div>
                                            <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-1">
                                                <span>vs. {opponentName}</span>
                                                {opponentIsProfile && <ShieldCheck size={12} className="text-green-400" title="Registered Player" />}
                                            </div>
                                        </div>

                                        <div className="font-semibold text-xl">
                                            <span>{myScore}</span>
                                            <span className="mx-1 text-slate-500">-</span>
                                            <span>{opponentScore}</span>
                                        </div>

                                        <div className="flex flex-col items-end">
                                             <div className={`font-bold text-lg ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                                                {isWin ? 'WIN' : 'LOSS'}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                                                {isForfeited && <span className="font-semibold text-yellow-500">Forfeit</span>}
                                                <Clock size={12}/>
                                                <span>{formatDate(match.completedAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                                )
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-slate-400">No completed matches yet. Play a game to see your history here!</p>
                    )}
                </div>
            </div>
            </div>
        </>
    );
};